import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { ApiError, getOrderDetail } from "@/lib/api-client";
import { useCustomerAuth } from "@/lib/auth/customer-auth-context";
import { orderStatusLabel, paymentStatusLabel, formatOrderDate } from "./order-format";

/**
 * Détail d'une commande du client connecté (`GET /orders/:orderNumber`) —
 * port de apps/web/app/(account)/compte/commandes/[orderNumber]/page.tsx.
 * Présentation inspirée de `app/(shop)/commande/confirmation/page.tsx` (mêmes
 * blocs Récapitulatif/Livraison/Paiement, cf. commentaire de l'original).
 *
 * `useParams` remplace `params: Promise<{ orderNumber }>` (Next.js) ;
 * chargement via `useQuery` avec distinction des trois issues d'erreur de
 * l'original (401 → session expirée, 404 → commande introuvable, autre →
 * message générique honnête plutôt qu'un plantage, §11.1).
 */
export function CompteCommandeDetailPage() {
  const { orderNumber = "" } = useParams<{ orderNumber: string }>();
  const { logout } = useCustomerAuth();

  const { data: order, error, isPending } = useQuery({
    queryKey: ["order", orderNumber],
    queryFn: () => getOrderDetail(orderNumber),
    enabled: orderNumber.length > 0,
  });

  if (error instanceof ApiError && error.status === 401) {
    logout();
    return <Navigate to="/connexion" replace />;
  }

  if (isPending) {
    return <p className="container-vvs py-16 text-center text-sm text-muted">Chargement de la commande…</p>;
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;

    return (
      <div className="container-vvs py-16 text-center">
        <p className="text-sm text-muted">
          {notFound
            ? `Commande ${orderNumber} introuvable.`
            : "Impossible de récupérer cette commande pour le moment."}
        </p>
        <Link to="/compte/commandes" className="mt-4 inline-block text-sm font-medium text-navy hover:text-gold">
          &larr; Retour à mes commandes
        </Link>
      </div>
    );
  }

  // Filet de sécurité pour le typage (`order` est `OrderDetail | undefined`
  // tant que la requête n'a ni réussi ni échoué) : les deux branches
  // `isPending`/`error` ci-dessus couvrent déjà tous les cas réels où
  // `order` serait absent — ce `return null` ne devrait donc jamais s'exécuter
  // en pratique, il ne sert qu'à prouver à TypeScript que `order` est défini
  // pour le reste du rendu.
  if (!order) return null;

  return (
    <div className="container-vvs flex flex-col gap-6 py-8 lg:py-12">
      <div>
        <Link to="/compte/commandes" className="text-sm font-medium text-navy hover:text-gold">
          &larr; Retour à mes commandes
        </Link>
      </div>

      <div className="flex flex-col gap-2 border border-line bg-white px-6 py-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="font-display text-xl font-semibold text-ink">Commande N° {order.orderNumber}</h1>
          <span className="inline-block rounded-sm border border-line bg-cream2 px-3 py-1 text-xs font-medium uppercase tracking-wide text-navy">
            {orderStatusLabel(order.status)}
          </span>
        </div>
        <p className="text-sm text-muted">Passée le {formatOrderDate(order.placedAt)}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 border border-line bg-white p-4 sm:grid-cols-3">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Récapitulatif</h2>
          <p className="mt-1 text-sm text-ink">
            {order.items.length} article{order.items.length > 1 ? "s" : ""}
          </p>
          <p className="text-sm text-ink">Sous-total {order.totals.subtotal.formatted}</p>
          {order.totals.discount.amountMinor > 0 ? (
            <p className="text-sm text-ink">Remise −{order.totals.discount.formatted}</p>
          ) : null}
          <p className="text-sm text-ink">Livraison {order.totals.shipping.formatted}</p>
          <p className="mt-1 text-sm font-semibold text-ink">Total {order.totals.grandTotal.formatted}</p>
        </div>

        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Livraison</h2>
          {order.shippingAddress ? (
            <>
              {typeof order.shippingAddress.recipientName === "string" ? (
                <p className="mt-1 text-sm text-ink">{order.shippingAddress.recipientName}</p>
              ) : null}
              {typeof order.shippingAddress.addressLine === "string" ? (
                <p className="text-sm text-ink">{order.shippingAddress.addressLine}</p>
              ) : null}
              {typeof order.shippingAddress.quartier === "string" ? (
                <p className="text-sm text-ink">{order.shippingAddress.quartier}</p>
              ) : null}
              {typeof order.shippingAddress.landmark === "string" && order.shippingAddress.landmark ? (
                <p className="text-sm text-muted">{order.shippingAddress.landmark}</p>
              ) : null}
            </>
          ) : (
            <p className="mt-1 text-sm text-muted">—</p>
          )}
        </div>

        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Paiement</h2>
          <p className="mt-1 text-sm text-ink">À la livraison</p>
          <p className="text-sm text-ink">{paymentStatusLabel(order.paymentStatus)}</p>
        </div>
      </div>

      <div className="border border-line bg-white p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Articles</h2>
        <ul className="mt-3 flex flex-col divide-y divide-line">
          {order.items.map((item, index) => (
            <li key={`${item.sku}-${index}`} className="flex items-center gap-4 py-3">
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt=""
                  className="h-14 w-14 flex-none rounded-sm border border-line object-cover"
                />
              ) : (
                <div className="h-14 w-14 flex-none rounded-sm border border-line bg-cream2" aria-hidden="true" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">{item.productName}</p>
                {item.variantName ? <p className="text-xs text-muted">{item.variantName}</p> : null}
                <p className="text-xs text-muted">Quantité {item.quantity}</p>
              </div>
              <p className="flex-none text-sm font-semibold text-ink">{item.lineTotal.formatted}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
